/**
 * Well Testing quotes (WT-5b, fieldiq-api#1258) on the shared quote screens:
 * the Well Testing Rate Card as a pricing source, "Create Well Testing Job" on
 * an accepted Well Testing quote, and every refusal those doors answer with as
 * a sentence. Pipeline's lib/pf-quotes.ts, rule for rule, on WT's card.
 *
 * Every helper answers for a Well Testing item, line or quote only and returns
 * null for anything else, so the shared screens keep exactly what they
 * rendered before for every other vertical.
 *
 * THE API DECIDES. Nothing here prices a line, picks a winning rate, or decides
 * which items may be quoted: an item's `refusal` is the API's (a rate tied with
 * another in the ticket's winning tier), and the screen only declines to offer it.
 */
import { wtApiError } from './well-testing';

export const WT_QUOTE_VERTICAL = 'well_testing';
export const WT_QUOTE_SOURCE_KIND = 'wt_rate';

/**
 * The terms beside a rate-card item, never priced: its code, kind, what it
 * applies to (a crew role, a package, a unit or type) and the card's notes.
 * Null for an item from any other source.
 */
export function wtQuoteItemTerms(item: { sourceKind?: string | null; terms?: Record<string, unknown> | null }): string | null {
  if (item.sourceKind !== WT_QUOTE_SOURCE_KIND) return null;
  const t = item.terms ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const parts = [str(t.code), str(t.kindLabel), str(t.appliesTo), str(t.notes)].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The currency a Well Testing quote's money is spelled in: the quote row's own
 * code. Null for any other vertical, which keeps fmtCurrency's bare "$" there.
 */
export function wtQuoteCurrency(quote: { vertical?: string | null; currency?: string | null } | null | undefined): string | null {
  if (!quote || quote.vertical !== WT_QUOTE_VERTICAL) return null;
  return quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
}

/**
 * The Type column for a line priced from the Well Testing Rate Card. The API
 * writes every such line as line_item_type 'other', so the kind is read from
 * the unit it stamps (api wtQuoteUnit). Null for any other line; a Well
 * Testing line with an unknown unit is `—`, never "Other".
 */
const WT_QUOTE_UNIT_KIND: Record<string, string> = {
  per_day: 'Per day',
  per_shift: 'Per shift',
  per_hour: 'Per hour',
  per_km: 'Per distance',
  per_mile: 'Per distance',
  per_volume: 'Per volume',
  flat_fee: 'Flat charge',
  each: 'Each',
};
export function wtQuoteLineKind(line: { source_kind?: string | null; unit?: string | null }): string | null {
  if (line.source_kind !== WT_QUOTE_SOURCE_KIND) return null;
  return (line.unit && WT_QUOTE_UNIT_KIND[line.unit]) || '—';
}

/**
 * Every refusal the two Well Testing quote doors answer with. `api: true` means
 * the API's own words win when it sent some; `api: false` codes answer in words
 * written for a developer ("well_id is a well of a different customer"), so
 * this sentence is always used.
 */
const WT_QUOTE_REFUSALS: Record<string, { api: boolean; sentence: string }> = {
  // POST /quotes/from-pricing-source
  QUOTE_RATE_AMBIGUOUS: { api: true, sentence: 'More than one rate on the Well Testing Rate Card applies to this line, so it cannot be quoted. Fix the rate card, then quote it.' },
  QUOTE_ITEM_NOT_AVAILABLE: { api: true, sentence: 'One of the picked rates no longer applies to this customer today. Go back and pick again.' },
  QUOTE_CURRENCY_MISMATCH: { api: true, sentence: "One of the picked rates is in a different currency from the company's, so it cannot be quoted." },
  QUOTE_QUANTITY_INVALID: { api: true, sentence: 'Enter a quantity greater than zero on every picked line.' },
  QUOTE_LINE_UNPRICED: { api: true, sentence: 'One of the picked lines has no rate on the Well Testing Rate Card, so it cannot be quoted.' },
  VERTICAL_NOT_ENABLED: { api: false, sentence: 'Well Testing is not turned on for this company, so it cannot be quoted.' },
  // POST /quotes/:id/create-wt-job
  QUOTE_NOT_FOUND: { api: false, sentence: 'This quote was not found. It may have been deleted.' },
  QUOTE_NOT_WELL_TESTING: { api: true, sentence: 'Only a Well Testing quote becomes a Well Testing job.' },
  QUOTE_NOT_ACCEPTED: { api: true, sentence: 'Accept the quote before creating its Well Testing job.' },
  QUOTE_CUSTOMER_REQUIRED: { api: true, sentence: 'The quote has no customer to create a Well Testing job for.' },
  WELL_REQUIRED: { api: false, sentence: "Pick the well this job is on. A Well Testing job always belongs to one of the customer's wells." },
  well_customer_mismatch: { api: false, sentence: "That well belongs to a different customer. Pick one of this quote's customer's wells." },
  not_found: { api: false, sentence: 'That well was not found. It may have been deleted. Pick the well again.' },
  QUOTE_JOB_ALREADY_CREATED: { api: true, sentence: "This quote's Well Testing job was just created. Refresh to see it." },
  SCHEDULED_DATE_INVALID: { api: false, sentence: 'The job day is not a valid date. Pick it again or leave it blank.' },
};

export const WT_QUOTE_REFUSAL_CODES = Object.keys(WT_QUOTE_REFUSALS);

/**
 * A refused Well Testing quote write as `{ code, error }` — the shape the quote
 * screens' refusal notice draws. A 403 is a refusal of permission whatever the
 * body says (the create-job door is admin / office staff), never a slip.
 */
export function wtQuoteRefusal(err: unknown, fallback: string): { code?: string; error: string } {
  const e = wtApiError(err);
  const code = e.code ?? undefined;
  if (e.status === 403 && code !== 'VERTICAL_NOT_ENABLED') {
    return { code, error: 'You do not have permission to do this. Ask an admin or office staff.' };
  }
  const known = code ? WT_QUOTE_REFUSALS[code] : undefined;
  if (known) return { code, error: known.api && e.message ? e.message : known.sentence };
  return { code, error: e.message ?? fallback };
}
